import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ModalVideo from "react-modal-video";
import "react-modal-video/css/modal-video.min.css";

import Home2Banner from "../components/Home2Banner";
import CounterBlog from "../components/CounterBlog";
import AboutServices, { HowItWorks, TrainingComparison } from "../components/AboutServices";
import Gallery from "../components/Gallery";
import Testimonial from "../components/Testimonial";
import PricingTable from "../components/PricingTable";

// IMPORTANT: OurBlog MUST be default export
import GymFAQ from "../components/OurBlog";

import ContactSection from "../components/ContactSection";
import { IMAGES } from "../constants/theme";
import { useAuth } from "../auth/AuthContext";
import apiClient from "../api/apiClient";
import SurveyGameDialog from "../components/SurveyGameDialog";
import { useLanguage } from "../i18n/LanguageContext";

const Home2 = () => {
  const [open, setOpen] = useState(false);
  const [surveyQuiz, setSurveyQuiz] = useState(null);
  const [showGuestInvitation, setShowGuestInvitation] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const location = useLocation().pathname;

  useEffect(() => {
    if (authLoading || user) {
      setShowGuestInvitation(false);
      return;
    }

    setShowGuestInvitation(
      sessionStorage.getItem("hpa-registration-invitation-dismissed") !== "true",
    );
  }, [authLoading, user]);

  useEffect(() => {
    if (authLoading || !user) {
      setShowWelcome(false);
      return undefined;
    }

    setShowWelcome(true);
    const timer = window.setTimeout(() => setShowWelcome(false), 6500);
    return () => window.clearTimeout(timer);
  }, [authLoading, user]);

  const dismissGuestInvitation = () => {
    sessionStorage.setItem("hpa-registration-invitation-dismissed", "true");
    setShowGuestInvitation(false);
  };

  useEffect(() => {
    if (authLoading || !user) {
      setSurveyQuiz(null);
      return;
    }

    let cancelled = false;

    apiClient.get("/survey-game")
      .then(({ data }) => {
        const dismissalKey = `hpa-survey-dismissed-${user.id}-${data.attempt_id}`;

        if (
          !cancelled
          && data.available
          && sessionStorage.getItem(dismissalKey) !== "true"
        ) {
          setSurveyQuiz(data);
        }
      })
      .catch(() => {
        if (!cancelled) setSurveyQuiz(null);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, user]);

  const dismissSurveyQuiz = () => {
    if (user && surveyQuiz?.attempt_id) {
      sessionStorage.setItem(
        `hpa-survey-dismissed-${user.id}-${surveyQuiz.attempt_id}`,
        "true",
      );
    }
    setSurveyQuiz(null);
  };

  // Set theme color ONCE (correct way)
  useEffect(() => {
    document.body.setAttribute("data-theme-color", "color_2");
    localStorage.setItem("theme", "color_2");
    localStorage.setItem("themeInd", 1);
  }, [location]);

  useEffect(() => {
    const revealItems = Array.from(document.querySelectorAll(".hpa-scroll-reveal"));
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion || !("IntersectionObserver" in window)) {
      revealItems.forEach((item) => item.classList.add("is-visible"));
      return undefined;
    }

    const revealFallback = window.setTimeout(() => {
      revealItems.forEach((item) => item.classList.add("is-visible"));
    }, 1200);

    const observer = new IntersectionObserver(
      (entries, activeObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          activeObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.16 }
    );

    revealItems.forEach((item, index) => {
      item.style.transitionDelay = `${Math.min(index * 70, 280)}ms`;
      observer.observe(item);
    });

    return () => {
      window.clearTimeout(revealFallback);
      observer.disconnect();
    };
  }, [location]);

  return (
    <>
      <ModalVideo
        channel="youtube"
        youtube={{ mute: 0, autoplay: 0 }}
        isOpen={open}
        videoId="X_9VoqR5ojM"
        onClose={() => setOpen(false)}
      />
      {surveyQuiz && (
        <SurveyGameDialog
          quiz={surveyQuiz}
          onClose={dismissSurveyQuiz}
        />
      )}
      {!authLoading && user && showWelcome && (
        <div className="hpa-welcome-back" role="status">
          <span>{t("Welcome back,")}</span>
          <strong>{user.name || t("Athlete")}!</strong>
          <small>{t("Ready to make today count?")}</small>
        </div>
      )}
      {showGuestInvitation && (
        <div className="hpa-invitation-backdrop" role="dialog" aria-modal="true" aria-labelledby="hpa-invitation-title">
          <div className="hpa-invitation-dialog">
            <button
              type="button"
              className="hpa-invitation-close"
              onClick={dismissGuestInvitation}
              aria-label="Close registration invitation"
            >
              &times;
            </button>
            <div className="hpa-invitation-icon" aria-hidden="true">
              <i className="fa-solid fa-trophy" />
            </div>
            <span className="hpa-invitation-kicker">{t("Your first win starts here")}</span>
            <h2 id="hpa-invitation-title">{t("Train smart. Play the challenge. Win a free month.")}</h2>
            <p>{t("Create your free account and take the Hybrid Performance Challenge for your chance to unlock a one-month subscription—on us.")}</p>
            <Link
              to="/auth?mode=signup"
              className="btn btn-primary btn-skew hpa-invitation-action"
              onClick={dismissGuestInvitation}
            >
              <span>{t("Register & Take the Challenge")}</span>
            </Link>
            <small>{t("No payment required to create your account.")}</small>
          </div>
        </div>
      )}

      <div className="page-content bg-white">
        {/* ================= Banner ================= */}
        <div className="main-bnr-three">
          <Home2Banner open={setOpen} />
        </div>

        {/* ================= Why HPA is different ================= */}
        <section id="why-different" className="content-inner-2 hpa-scroll-reveal">
          <div className="container">
            <TrainingComparison />
          </div>
        </section>

        <section id="how-it-works" className="content-inner-2 hpa-scroll-reveal">
          <div className="container">
            <HowItWorks />
          </div>
        </section>

        {/* ================= Counters ================= */}
        <section className="counter-wrapper1 hpa-counter-section hpa-scroll-reveal">
          <div className="container">
            <div className="counter-inner hpa-counter-inner">
              <div className="counter-blog-header">
                <span>{t("Measured progress")}</span>
                <h2>{t("Numbers that rise with the work.")}</h2>
              </div>
              <div>
                <CounterBlog />
              </div>
            </div>
          </div>
        </section>

        {/* ================= Motivation Video ================= */}
        <section className="hpa-motivation-video hpa-scroll-reveal" aria-labelledby="motivation-video-heading">
          <div className="container">
            <div className="hpa-motivation-shell">
              <div className="hpa-motivation-copy">
                <span className="hpa-motivation-kicker">{t("Daily standard")}</span>
                <h2 id="motivation-video-heading">
                  {t("Move with purpose. Train with intent.")}
                </h2>
                <p>
                  {t("Push through every rep, lock in your discipline, and turn every workout into proof of what you are building.")}
                </p>
              </div>

              <div className="hpa-motivation-frame" aria-label="Hybrid Performance Academy motivation video">
                <video
                  className="hpa-motivation-media"
                  src="/videos/motivational-no-text.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
                <div className="hpa-motivation-shade" />
          
              </div>
            </div>
          </div>
        </section>
        {/* ================= Gallery ================= */}
        <section
          className="content-inner overflow-hidden hpa-scroll-reveal hpa-dark-panel-section hpa-libraries-section"
          style={{
            backgroundImage: `url(${IMAGES.BgImage12})`,
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="container">
            <div className="hpa-dark-content-panel hpa-libraries-panel">
              <Gallery />
            </div>
          </div>
        </section>

        <section id="packages" className="content-inner hpa-dark-panel-section hpa-packages-section hpa-scroll-reveal">
          <div className="container">
            <div className="hpa-dark-content-panel hpa-packages-panel">
              <div className="section-head style-1 text-center hpa-plans-heading">
                <h2 className="title">
                  {t("OUR PLANS")}
                </h2>
              </div>
              <PricingTable />
            </div>
          </div>
        </section>
        {/* ================= Testimonials ================= */}
        <section
          className="content-inner-1 overflow-hidden testimonial-wrapper1 hpa-scroll-reveal"
          data-text="FEEDBACK"
          style={{
            backgroundImage: `url(${IMAGES.BgImage13})`,
            backgroundRepeat: "no-repeat",
          }}
        >
         
          <div className="container">

             {/* ================= Testimonial ================= */}
            <div className="section-head style-1 text-center">
              <h2 className="title">
                {t("What Clients Say")}
              </h2>
            </div>
            <Testimonial />
          </div>
        </section>

        {/* ================= FAQ / Blog ================= */}
        <section
          className="content-inner-2 overflow-hidden hpa-scroll-reveal"
          style={{
            backgroundImage: `url(${IMAGES.BgImage1})`,
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="container">
            <div className="section-head style-1 text-center">
              <h2 className="title">
                {t("Gym FAQs")}
              </h2>
            </div>
            <GymFAQ />
          </div>
        </section>

       

    


        {/* ================= About / What You Get ================= */}
        <section id="about" className="about-bx4 content-inner-2 hpa-scroll-reveal">
          <div className="container">
            <AboutServices showHowItWorks={false} showComparison={false} />
          </div>
        </section>

        {/* ================= Contact ================= */}
        <section id="contact" className="content-inner-2 hpa-scroll-reveal" style={{ backgroundImage: `url(${IMAGES.BgImage1})`, backgroundRepeat: "no-repeat" }}>
          <ContactSection />
        </section>
      </div>

      <style>{`
        .hpa-scroll-reveal {
          opacity: 0;
          transform: translateY(28px);
          transition:
            opacity 700ms ease,
            transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hpa-scroll-reveal.is-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .hpa-motivation-video {
          position: relative;
          overflow: hidden;
          padding: clamp(68px, 8vw, 118px) 0;
          background:
            radial-gradient(circle at 18% 18%, rgba(255, 72, 0, 0.28), transparent 28%),
            linear-gradient(135deg, #080808 0%, #17110f 48%, #050505 100%);
          isolation: isolate;
        }
        .hpa-motivation-video::before,
        .hpa-motivation-video::after {
          position: absolute;
          inset: auto;
          z-index: -1;
          content: "";
          pointer-events: none;
        }
        .hpa-motivation-video::before {
          top: 10%;
          right: -8%;
          width: 34vw;
          min-width: 280px;
          aspect-ratio: 1;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 999px;
          animation: hpaPulseRing 7s ease-in-out infinite;
        }
        .hpa-motivation-video::after {
          left: 0;
          right: 0;
          bottom: 0;
          height: 46%;
          background: linear-gradient(0deg, rgba(255, 72, 0, 0.16), transparent);
        }
        .hpa-motivation-shell {
          display: grid;
          grid-template-columns: minmax(260px, 0.43fr) minmax(0, 0.57fr);
          align-items: center;
          gap: clamp(28px, 5vw, 64px);
        }
        .hpa-motivation-copy {
          color: #fff;
        }
        .hpa-motivation-kicker {
          display: inline-flex;
          margin-bottom: 16px;
          color: var(--primary);
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }
        .hpa-motivation-copy h2 {
          margin: 0;
          color: #fff;
          font-size: clamp(36px, 5vw, 74px);
          line-height: 0.95;
          letter-spacing: 0;
          text-transform: uppercase;
        }
        .hpa-motivation-copy p {
          max-width: 520px;
          margin: 22px 0 0;
          color: rgba(255, 255, 255, 0.72);
          font-size: clamp(15px, 1.4vw, 18px);
          line-height: 1.75;
        }
        .hpa-motivation-frame {
          position: relative;
          min-height: clamp(420px, 48vw, 660px);
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: 28px;
          background: #111;
          box-shadow: 0 34px 95px rgba(0, 0, 0, 0.38);
          transform: rotate(1.2deg);
          transition: transform 650ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 650ms ease;
        }
        .hpa-motivation-frame:hover {
          box-shadow: 0 42px 110px rgba(255, 72, 0, 0.2), 0 34px 95px rgba(0, 0, 0, 0.42);
          transform: rotate(0deg) translateY(-8px);
        }
        .hpa-motivation-media {
          width: 100%;
          height: 100%;
          min-height: inherit;
          object-fit: cover;
          transform: scale(1.035);
          filter: saturate(1.1) contrast(1.04);
          animation: hpaVideoFloat 10s ease-in-out infinite alternate;
        }
        .hpa-motivation-shade {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(90deg, rgba(0, 0, 0, 0.62), transparent 42%),
            linear-gradient(0deg, rgba(0, 0, 0, 0.62), transparent 48%);
          pointer-events: none;
        }
        .hpa-motivation-badge {
          position: absolute;
          display: grid;
          gap: 2px;
          min-width: 138px;
          padding: 14px 16px;
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 18px;
          color: #fff;
          background: rgba(10, 10, 10, 0.54);
          backdrop-filter: blur(14px);
          box-shadow: 0 18px 42px rgba(0, 0, 0, 0.26);
        }
        .hpa-motivation-badge span {
          color: var(--primary);
          font-size: 12px;
          font-weight: 800;
        }
        .hpa-motivation-badge strong {
          font-size: 18px;
          line-height: 1.1;
        }
        .hpa-motivation-badge-top {
          top: 24px;
          left: 24px;
          animation: hpaBadgeDrift 5.8s ease-in-out infinite;
        }
        .hpa-motivation-badge-bottom {
          right: 24px;
          bottom: 24px;
          animation: hpaBadgeDrift 6.4s ease-in-out infinite reverse;
        }
        @keyframes hpaVideoFloat {
          from { transform: scale(1.035) translate3d(0, 0, 0); }
          to { transform: scale(1.075) translate3d(-1.6%, -1.2%, 0); }
        }
        @keyframes hpaBadgeDrift {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes hpaPulseRing {
          0%, 100% { transform: scale(1); opacity: 0.45; }
          50% { transform: scale(1.08); opacity: 0.2; }
        }
        @media (max-width: 991px) {
          .hpa-motivation-shell {
            grid-template-columns: 1fr;
          }
          .hpa-motivation-copy {
            text-align: center;
          }
          .hpa-motivation-copy p {
            margin-inline: auto;
          }
          .hpa-motivation-frame {
            min-height: clamp(360px, 70vw, 560px);
            transform: none;
          }
        }
        @media (max-width: 767px) {
          .about-bx4.hpa-scroll-reveal,
          .about-bx4.hpa-scroll-reveal.is-visible,
          .about-bx4 .wow {
            opacity: 1 !important;
            visibility: visible !important;
            transform: none !important;
            animation: none !important;
          }
          .about-bx4.content-inner-2 {
            padding-top: 54px;
            padding-bottom: 54px;
          }
          .about-bx4 .dz-media {
            margin-top: 18px !important;
            margin-bottom: 32px;
          }
          .about-bx4 .about-content {
            margin-top: 10px;
          }
        }
        @media (max-width: 575px) {
          .hpa-motivation-video {
            padding: 54px 0;
          }
          .hpa-motivation-frame {
            min-height: 62vh;
            border-radius: 20px;
          }
          .hpa-motivation-badge {
            min-width: 112px;
            padding: 11px 12px;
            border-radius: 14px;
          }
          .hpa-motivation-badge-top {
            top: 14px;
            left: 14px;
          }
          .hpa-motivation-badge-bottom {
            right: 14px;
            bottom: 14px;
          }
          .hpa-motivation-badge strong {
            font-size: 15px;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hpa-scroll-reveal {
            opacity: 1;
            transform: none;
            transition: none;
          }
          .hpa-motivation-video::before,
          .hpa-motivation-media,
          .hpa-motivation-badge {
            animation: none;
          }
          .hpa-motivation-frame,
          .hpa-motivation-frame:hover {
            transform: none;
          }
        }
      `}</style>
    </>
  );
};

export default Home2;
